import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiSend,
  FiCheck,
  FiX,
  FiUpload,
  FiRefreshCw,
  FiUsers,
  FiPaperclip,
  FiTrash2,
} from "react-icons/fi";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import adminService from "../../../api/services/admin";

import {
  SectionHeader,
  SectionTitle,
  SectionActions,
  Button,
} from "../AdminUsers/AdminUsers.styles";
import {
  Container,
  Section,
  SectionSubTitle,
  FormGrid,
  FormGroup,
  Label,
  Input,
  TextArea,
  FileUploadWrapper,
  FileInput,
  FileLabel,
  EmailCount,
  AttachmentPreview,
  AttachmentThumb,
  AttachmentInfo,
  RemoveAttachmentButton,
  TestEmailWrapper,
  ProgressWrapper,
  ProgressBar,
  ProgressFill,
  ProgressText,
  Results,
  ResultsTitle,
  ResultsStats,
  Stat,
  StatLabel,
  StatValue,
  ErrorList,
  ErrorItem,
  EmailPreview,
  EmailList,
  EmailItem,
  ActionButton,
  PreviewCard,
  PreviewHeader,
  PreviewContent,
} from "./AdminPromoEmailSender.styles";
import {
  selectPromoEmailsLoading,
  selectPromoEmailResults,
  selectPromoEmailProgress,
} from "../../../store/slices/promoEmails/slice";
import {
  sendPromoEmails,
  sendTestEmail,
  clearResults,
} from "../../../store/slices/promoEmails/asyncThunks";
import { useAdminModal } from "../../../contexts/AdminModalContext";

const AdminPromoEmailSender = () => {
  const dispatch = useDispatch();
  const loading = useSelector(selectPromoEmailsLoading);
  const results = useSelector(selectPromoEmailResults);
  const progress = useSelector(selectPromoEmailProgress);
  const { showConfirmModal } = useAdminModal();

  const [emails, setEmails] = useState([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [promoCode, setPromoCode] = useState("");
  const [discount, setDiscount] = useState("");
  const [validDays, setValidDays] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [emailSubject, setEmailSubject] = useState(
    "LASH & BROW LIFT BIBLE ONLINE EDUKACIJA",
  );
  const [emailMessage, setEmailMessage] = useState(`Draga učenice, 🤍

Spremila sam nešto posebno za tebe! ✨
Izašla je nova Lash & Brow Lift Bible online edukacija.

Iskoristi kod {PROMO_CODE} i ostvari {DISCOUNT}% popusta na sve moje kurseve. 🎓

Akcija traje do {EXPIRY_DATE} zato je sada pravi trenutak da investiraš u sebe. ⏳

Ne propusti ovu priliku da proširiš svoje znanje uz značajan popust i podigneš svoje usluge na viši nivo. 💫

Sa poštovanjem,
Maša Mitov
MITOVKA`);

  // Format email preview sa placeholderima
  const formatEmailPreview = (message) => {
    const days = parseInt(validDays);
    let expiryText = "EXPIRY_DATE";
    if (!isNaN(days) && days > 0) {
      const expiryDate = new Date();
      expiryDate.setDate(expiryDate.getDate() + days);
      expiryText = expiryDate.toLocaleDateString("sr-latn-RS");
    }

    return message
      .replace(/{PROMO_CODE}/g, promoCode || "PROMO_CODE")
      .replace(/{DISCOUNT}/g, discount || "0")
      .replace(/{EXPIRY_DATE}/g, expiryText);
  };

  // Učitavanje Excel fajla
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 });

        // Ekstrahuj email adrese iz kolona
        const extractedEmails = new Set();

        data.forEach((row) => {
          row.forEach((cell) => {
            if (cell && typeof cell === "string" && cell.includes("@")) {
              extractedEmails.add(cell.trim().toLowerCase());
            }
          });
        });

        const emailArray = Array.from(extractedEmails).filter((email) =>
          /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
        );

        if (emailArray.length === 0) {
          toast.error("U fajlu nije pronađena nijedna validna email adresa");
          return;
        }

        setEmails(emailArray);
        toast.success(`Učitano ${emailArray.length} email adresa`);
      } catch (error) {
        toast.error("Greška pri učitavanju fajla: " + error.message);
      }
    };
    reader.readAsBinaryString(file);
  };

  // Maksimalna veličina slike (5MB pre base64 enkodovanja; server limit je 10MB)
  const MAX_ATTACHMENT_SIZE = 5 * 1024 * 1024;

  // Formatiranje veličine fajla za prikaz
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  // Učitavanje slike koja se prilaže uz email
  const handleAttachmentUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Dozvoljene su samo slike");
      e.target.value = "";
      return;
    }

    if (file.size > MAX_ATTACHMENT_SIZE) {
      toast.error("Slika je prevelika (maksimalno 5MB)");
      e.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      const dataUrl = evt.target.result;
      // Odvoji čist base64 sadržaj od "data:...;base64," prefiksa
      const base64 = dataUrl.split(",")[1];
      setAttachment({
        filename: file.name,
        contentType: file.type,
        size: file.size,
        data: base64,
        previewUrl: dataUrl,
      });
      toast.success(`Slika "${file.name}" priložena`);
    };
    reader.onerror = () => {
      toast.error("Greška pri učitavanju slike");
    };
    reader.readAsDataURL(file);

    // Dozvoli ponovni izbor istog fajla
    e.target.value = "";
  };

  // Uklanjanje priložene slike
  const handleRemoveAttachment = () => {
    setAttachment(null);
  };

  // Učitavanje email adresa svih registrovanih korisnika direktno iz baze
  const handleLoadUsersFromDb = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await adminService.getAllUserEmails();
      const dbEmails = (response.emails || [])
        .map((email) => String(email).trim().toLowerCase())
        .filter((email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email));

      if (dbEmails.length === 0) {
        toast.error("Nema registrovanih korisnika u bazi");
        return;
      }

      setEmails([...new Set(dbEmails)]);
      toast.success(`Učitano ${dbEmails.length} korisnika iz baze`);
    } catch (error) {
      toast.error("Greška pri učitavanju korisnika iz baze");
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Slanje test email-a
  const handleSendTestEmail = async () => {
    if (!testEmail) {
      toast.error("Unesi test email adresu");
      return;
    }

    try {
      await dispatch(
        sendTestEmail({
          email: testEmail,
          promoCode,
          discount,
          validDays,
          emailSubject,
          emailMessage,
          attachment: attachment
            ? {
                filename: attachment.filename,
                contentType: attachment.contentType,
                data: attachment.data,
              }
            : null,
        }),
      ).unwrap();
      toast.success("Test email uspešno poslat!");
    } catch (error) {
      toast.error("Greška pri slanju test email-a");
    }
  };

  // Slanje svih email-ova
  const handleSendEmails = async () => {
    if (emails.length === 0) {
      toast.error("Nema email adresa za slanje");
      return;
    }

    if (!emailSubject.trim()) {
      toast.error("Naslov email-a ne sme biti prazan");
      return;
    }

    // Spreči slanje poruke sa nepopunjenim placeholderima
    if (emailMessage.includes("{PROMO_CODE}") && !promoCode.trim()) {
      toast.error(
        "Poruka sadrži {PROMO_CODE}, a promo kod nije unet. Unesi promo kod ili ukloni placeholder iz poruke.",
      );
      return;
    }
    if (emailMessage.includes("{DISCOUNT}") && !discount) {
      toast.error(
        "Poruka sadrži {DISCOUNT}, a popust nije unet. Unesi popust ili ukloni placeholder iz poruke.",
      );
      return;
    }
    if (emailMessage.includes("{EXPIRY_DATE}") && !validDays) {
      toast.error(
        "Poruka sadrži {EXPIRY_DATE}, a broj dana važenja nije unet. Unesi broj dana ili ukloni placeholder iz poruke.",
      );
      return;
    }

    showConfirmModal(
      "Potvrda slanja",
      `Da li želiš da pošalješ email na ${emails.length} adresa?`,
      handleConfirmSend,
      "Pošalji",
      "Otkaži",
    );
  };

  const handleConfirmSend = async () => {
    try {
      await dispatch(
        sendPromoEmails({
          emails,
          promoCode,
          discount,
          validDays,
          emailSubject,
          emailMessage,
          attachment: attachment
            ? {
                filename: attachment.filename,
                contentType: attachment.contentType,
                data: attachment.data,
              }
            : null,
        }),
      ).unwrap();

      toast.success("Email-ovi uspešno poslati!");
    } catch (error) {
      toast.error("Greška pri slanju email-ova");
    }
  };

  const handleClearResults = () => {
    // Reset everything to initial state
    dispatch(clearResults());
    setEmails([]);
    setAttachment(null);
    setPromoCode("");
    setDiscount("");
    setValidDays("");
    setTestEmail("");
    setEmailSubject("LASH & BROW LIFT BIBLE ONLINE EDUKACIJA");
    setEmailMessage(`Draga učenice, 🤍

Spremila sam nešto posebno za tebe! ✨
Izašla je nova Lash & Brow Lift Bible online edukacija.

Iskoristi kod {PROMO_CODE} i ostvari {DISCOUNT}% popusta na sve moje kurseve. 🎓

Akcija traje do {EXPIRY_DATE} zato je sada pravi trenutak da investiraš u sebe. ⏳

Ne propusti ovu priliku da proširiš svoje znanje uz značajan popust i podigneš svoje usluge na viši nivo. 💫

Sa poštovanjem,
Maša Mitov
MITOVKA`);
    toast.success("Sve je resetovano na početno stanje!");
  };

  return (
    <>
      <SectionHeader>
        <SectionTitle>Email Marketing</SectionTitle>
        <SectionActions>
          {results && (
            <Button $secondary onClick={handleClearResults}>
              <FiRefreshCw />
              Resetuj
            </Button>
          )}
        </SectionActions>
      </SectionHeader>

      <Container>
        <Section>
          <SectionSubTitle>1. Učitaj email adrese</SectionSubTitle>
          <FileUploadWrapper>
            <FileInput
              type="file"
              accept=".xlsx, .xls"
              onChange={handleFileUpload}
              id="file-upload"
            />
            <FileLabel htmlFor="file-upload">
              <FiUpload size={20} />
              Izaberi Excel fajl
            </FileLabel>
            <ActionButton
              type="button"
              onClick={handleLoadUsersFromDb}
              disabled={isLoadingUsers}
              $primary
            >
              <FiUsers />
              {isLoadingUsers
                ? "Učitavam..."
                : "Učitaj sve registrovane korisnike"}
            </ActionButton>
            {emails.length > 0 && (
              <EmailCount>
                <FiCheck color="#28a745" />
                {emails.length} email adresa učitano
              </EmailCount>
            )}
          </FileUploadWrapper>
        </Section>

        <Section>
          <SectionSubTitle>
            2. Podesi promo kod detalje (opciono)
          </SectionSubTitle>
          <FormGrid>
            <FormGroup>
              <Label>Promo Kod</Label>
              <Input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="BACKTOSCHOOL"
              />
            </FormGroup>
            <FormGroup>
              <Label>Popust (%)</Label>
              <Input
                placeholder="30"
                type="number"
                value={discount}
                onChange={(e) => setDiscount(e.target.value)}
                min="1"
                max="100"
              />
            </FormGroup>
            <FormGroup>
              <Label>Važi (dana)</Label>
              <Input
                placeholder="7"
                type="number"
                value={validDays}
                onChange={(e) => setValidDays(e.target.value)}
                min="1"
              />
            </FormGroup>
          </FormGrid>
        </Section>

        <Section>
          <SectionSubTitle>3. Kreiraj Email Poruku</SectionSubTitle>
          <FormGrid style={{ gridTemplateColumns: "1fr" }}>
            <FormGroup>
              <Label>Naslov Email-a</Label>
              <Input
                type="text"
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                placeholder="Ekskluzivna ponuda samo za vas!"
              />
            </FormGroup>
            <FormGroup>
              <Label>
                Email Poruka (koristite {"{PROMO_CODE}"}, {"{DISCOUNT}"},{" "}
                {"{EXPIRY_DATE}"} za automatsku zamenu)
              </Label>
              <TextArea
                value={emailMessage}
                onChange={(e) => setEmailMessage(e.target.value)}
                placeholder="Unesite vašu email poruku ovde..."
              />
            </FormGroup>
          </FormGrid>

          <FormGroup style={{ marginTop: 20 }}>
            <Label>Prilog - slika (opciono)</Label>
            <FileUploadWrapper>
              <FileInput
                type="file"
                accept="image/*"
                onChange={handleAttachmentUpload}
                id="attachment-upload"
              />
              <FileLabel htmlFor="attachment-upload">
                <FiPaperclip size={18} />
                Izaberi sliku
              </FileLabel>
            </FileUploadWrapper>
            {attachment && (
              <AttachmentPreview>
                <AttachmentThumb
                  src={attachment.previewUrl}
                  alt={attachment.filename}
                />
                <AttachmentInfo>
                  <span className="name">{attachment.filename}</span>
                  <span className="size">{formatFileSize(attachment.size)}</span>
                </AttachmentInfo>
                <RemoveAttachmentButton
                  type="button"
                  onClick={handleRemoveAttachment}
                  title="Ukloni sliku"
                >
                  <FiTrash2 size={18} />
                </RemoveAttachmentButton>
              </AttachmentPreview>
            )}
          </FormGroup>

          <PreviewCard>
            <PreviewHeader>
              <h4>📧 Email Preview</h4>
              <div className="subject">
                <strong>Naslov:</strong> {emailSubject}
              </div>
            </PreviewHeader>
            <PreviewContent>{formatEmailPreview(emailMessage)}</PreviewContent>
          </PreviewCard>
        </Section>

        <Section>
          <SectionSubTitle>4. Test Email (opciono)</SectionSubTitle>
          <TestEmailWrapper>
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="tvoj@email.com"
              style={{ flex: 1 }}
            />
            <ActionButton
              onClick={handleSendTestEmail}
              disabled={loading || !testEmail}
              $primary
            >
              <FiSend />
              Pošalji Test
            </ActionButton>
          </TestEmailWrapper>
        </Section>

        <Section>
          <SectionSubTitle>5. Pošalji svima</SectionSubTitle>
          <ActionButton
            onClick={handleSendEmails}
            disabled={emails.length === 0 || loading}
            $primary
            $fullWidth
          >
            <FiSend />
            {loading ? "Šaljem..." : `Pošalji ${emails.length} Email-ova`}
          </ActionButton>

          {loading && progress && (
            <ProgressWrapper>
              <ProgressBar>
                <ProgressFill
                  style={{
                    width: `${(progress.current / progress.total) * 100}%`,
                  }}
                  $isComplete={progress.current === progress.total}
                />
              </ProgressBar>
              <ProgressText>
                {progress.current}/{progress.total} poslato
                {progress.failed > 0 && ` (${progress.failed} neuspešno)`}
              </ProgressText>
            </ProgressWrapper>
          )}

          {results && (
            <Results $success={results.failed === 0}>
              <ResultsTitle $success={results.failed === 0}>
                {results.failed === 0 ? (
                  <>
                    <FiCheck size={24} />
                    Uspešno poslato!
                  </>
                ) : (
                  <>
                    <FiX size={24} />
                    Završeno sa greškama
                  </>
                )}
              </ResultsTitle>
              <ResultsStats>
                <Stat>
                  <StatLabel>Ukupno:</StatLabel>
                  <StatValue>{results.total}</StatValue>
                </Stat>
                <Stat $success>
                  <StatLabel $success>Poslato:</StatLabel>
                  <StatValue $success>{results.sent}</StatValue>
                </Stat>
                <Stat $error>
                  <StatLabel $error>Neuspešno:</StatLabel>
                  <StatValue $error>{results.failed}</StatValue>
                </Stat>
              </ResultsStats>

              {results.errors.length > 0 && (
                <ErrorList>
                  <h4>Greške:</h4>
                  {results.errors.slice(0, 10).map((err, idx) => (
                    <ErrorItem key={idx}>
                      {err.email}: {err.error}
                    </ErrorItem>
                  ))}
                  {results.errors.length > 10 && (
                    <p>... i još {results.errors.length - 10} grešaka</p>
                  )}
                </ErrorList>
              )}
            </Results>
          )}
        </Section>

        {emails.length > 0 && (
          <EmailPreview>
            <h3>Preview Email Lista ({emails.length}):</h3>
            <EmailList>
              {/* {emails.slice(0, 10).map((email, idx) => (
                <EmailItem key={idx}>{email}</EmailItem>
              ))}
              {emails.length > 10 && (
                <EmailItem>... i još {emails.length - 10} email-ova</EmailItem>
              )} */}
              {emails.map((email, idx) => (
                <EmailItem key={idx}>
                  {idx + 1}. {email}
                </EmailItem>
              ))}
            </EmailList>
          </EmailPreview>
        )}
      </Container>
    </>
  );
};

export default AdminPromoEmailSender;
